/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// JS bridge to the native DriveTelemetryPlugin (iOS only in Phase 1;
// ios/App/App/DriveTelemetryPlugin.swift). The plugin OBSERVES the automotive
// motion-activity signal and, when a trip ends, emits a single `tripSummary`
// event with the full per-trip telemetry. JS never polls — it adds a listener.
//
// Web/Android no-op: registerPlugin returns a proxy that throws at call time;
// every caller guards with a Capacitor platform check (see src/services/
// driveTelemetry.ts) so production never hits that throw.

import { registerPlugin } from "@capacitor/core";
import type { TripSummary } from "@/utils/driveTelemetry/driveRating";

export interface DriveTelemetryPlugin {
  /** Start observing automotive activity; trips auto start/stop natively. */
  startMonitoring(): Promise<{ monitoring: boolean }>;
  /** Stop observing; ends any in-flight trip WITHOUT emitting a summary. */
  stopMonitoring(): Promise<{ monitoring: boolean }>;
  /**
   * Drain the native queue of completed trip summaries and RETURN them. Trips
   * almost always end with the WebView suspended (background), so summaries are
   * persisted to a native queue rather than pushed live (a live notify would be
   * lost). JS calls this on every foreground/start; the native queue is cleared
   * only as this resolve carries the data across the bridge, so nothing is lost
   * to a suspended WebView.
   */
  flushPending(): Promise<{ summaries: TripSummary[] }>;
}

export const DriveTelemetry =
  registerPlugin<DriveTelemetryPlugin>("DriveTelemetry");
